import { useEffect, useState, useMemo } from 'react';
import { createRoute, redirect, useParams } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Route as rootRoute } from '../../__root';
import { API_BASE_URL } from '@/client/config';
import { StreamDetailView, StreamDetailData } from '@/client/components/stream-detail-view';
import { generateStreamingURIs } from '@/client/lib/format';
import type { EventFullDetailsResponse } from '@shared/types';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'events/upcoming-streams/$streamId',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: UpcomingStreamDetailComponent,
});

function UpcomingStreamDetailComponent() {
  // streamId is in format: clientID_eventID
  const { streamId } = useParams({ from: '/events/upcoming-streams/$streamId' });
  const { getAccessTokenSilently } = useAuth0();

  const [eventData, setEventData] = useState<EventFullDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Parse clientID and eventID from streamId
  const [clientID, eventID] = useMemo(() => {
    const parts = streamId.split('_');
    if (parts.length >= 2) {
      return [parts[0], parts.slice(1).join('_')];
    }
    return [null, streamId];
  }, [streamId]);

  // Fetch event details from API
  useEffect(() => {
    async function fetchEventDetails() {
      if (!clientID || !eventID) {
        setError('Invalid stream ID');
        setLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${API_BASE_URL}/api/events/${clientID}/${eventID}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 404) {
            setError('Event not found');
          } else {
            setError('Failed to load event details');
          }
          setLoading(false);
          return;
        }

        const data: EventFullDetailsResponse = await response.json();
        setEventData(data);
      } catch (err) {
        console.error('Error fetching event details:', err);
        setError('Failed to load event details');
      } finally {
        setLoading(false);
      }
    }

    fetchEventDetails();
  }, [clientID, eventID, getAccessTokenSilently]);

  // Transform API response to StreamDetailData format
  const stream: StreamDetailData | null = useMemo(() => {
    if (!eventData) return null;

    return {
      clientID: eventData.clientID,
      clientName: eventData.clientName,
      eventID: eventData.eventID,
      eventTitle: eventData.eventTitle,
      status: eventData.status,
      streamStatus: eventData.streamStatus,

      // Schedule
      eventStartDateTime: eventData.eventStartDateTime,
      eventEndDateTime: eventData.eventEndDateTime,
      startDateTime: eventData.startDateTime,
      endDateTime: null,
      autoStart: eventData.autoStart,
      autoStop: eventData.autoStop,
      preroll: eventData.preroll,

      // Metadata
      customID: eventData.customID,
      description: eventData.description,
      notes: eventData.notes,
      private: eventData.private,
      liveToBreak: eventData.liveToBreak,

      // Assignment
      encoderID: eventData.encoderID,

      // Infrastructure (from liveStreams when active)
      liveID: eventData.liveID,
      ingInstanceID: eventData.ingInstanceID,
      egInstanceID: eventData.egInstanceID,
      wcStreamID: eventData.wcStreamID,

      // Generate streaming URIs for upcoming stream
      streamingURIs: clientID && eventID ? generateStreamingURIs(clientID, eventID) : null,
    };
  }, [eventData, clientID, eventID]);

  // Handle refresh
  const handleRefresh = async () => {
    setLoading(true);
    setError(null);

    if (!clientID || !eventID) {
      setError('Invalid stream ID');
      setLoading(false);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/events/${clientID}/${eventID}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        setError('Failed to refresh event details');
        setLoading(false);
        return;
      }

      const data: EventFullDetailsResponse = await response.json();
      setEventData(data);
    } catch (err) {
      console.error('Error refreshing event details:', err);
      setError('Failed to refresh event details');
    } finally {
      setLoading(false);
    }
  };

  // If there's an error, show it via the StreamDetailView's not found state
  if (error && !loading) {
    return (
      <StreamDetailView
        stream={null}
        loading={false}
        backTo="/events/upcoming-streams"
        backLabel="Back to Upcoming Streams"
        streamId={streamId}
        isLiveStream={false}
      />
    );
  }

  return (
    <StreamDetailView
      stream={stream}
      loading={loading}
      backTo="/events/upcoming-streams"
      backLabel="Back to Upcoming Streams"
      onRefresh={handleRefresh}
      streamId={streamId}
      isLiveStream={false}
    />
  );
}
